import { useMemo, useState } from 'react';
import { PerformanceOverview } from '@/features/performance/PerformancePanel';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Gauge, Monitor, RefreshCw, Star, Wallet } from 'lucide-react';
import { adminDashboard, adminMainAccount, adminRealAccount } from '@/api/admin';
import type {
  DashboardAppUsersToday,
  DashboardDetachedState,
  DashboardMainAccount,
  LiveAccountSummary,
  MainAccountPosition,
  Money,
} from '@/api/types';
import type { RealAccountCapacity } from '@/api/extras';
import { ApiError, shouldRetry } from '@/lib/http';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KpiCard } from '@/components/widgets/KpiCard';
import { ProgressBar } from '@/components/widgets/ProgressBar';
import { MarginGauge } from '@/components/widgets/MarginGauge';
import { Pill } from '@/components/widgets/Pill';
import { ForceLiquidateButton } from '@/features/force-liquidate';
import { DetachedBadge, normalizeDetachState } from '@/features/detach';
import { SymbolSpec } from '@/features/live-screen/SymbolSpec';
import { RecentSignalsStream } from '@/features/live-screen/RecentSignalsStream';
import { CandleChart, type Candle } from '@/features/live-screen/CandleChart';
import { PositionsTable, type MainPosition } from '@/features/live-screen/PositionsTable';
import { UserPositionsTable } from '@/features/live-screen/UserPositionsTable';
import { computePositionLive, computeNetPnl } from '@/features/live-screen/position-pnl';
import { useQuoteStream, usePriceFlash } from '@/realtime/use-quotes';
import { useQuoteStore } from '@/stores/quotes';
import { fmtDateTime, fmtMoney, fmtNum, fmtPct, fmtPnLNumber, moneyToNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/** 主账户未配置/未连接 → 后端返 503;据此做"主账户未连接"降级而非崩溃 */
function isMainAccountUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 503;
}

type ChartPeriod = 'm1' | 'm5' | 'm15';
const CHART_PERIODS: Array<{ value: ChartPeriod; label: string }> = [
  { value: 'm1', label: 'M1' },
  { value: 'm5', label: 'M5' },
  { value: 'm15', label: 'M15' },
];

// inverted=true(净值水位,越高越健康):颜色锚定后台可配下限 floor(= 后端急停线),不再写死 70/85。
// ceiling(当日亏损,越高越危险):颜色锚定后台可配上限(= 后端急停线 maxDailyLossPercent);不传时
// 保持旧的固定 80/50 通用占用带(保证金/总持仓/API 错误率不受影响);ceiling 分支下 floor 形参不
// 生效,调用处按位置传参占位即可。0.7 预警提前量是独立选的经验值,不是复用 capacity.rs 的 50/70/85。
const riskTone = (pct: number, inverted = false, floor = 70, ceiling?: number): 'brand' | 'warn' | 'down' => {
  if (inverted) {
    if (floor <= 0) return 'brand';
    return pct < floor ? 'down' : pct < floor + 15 ? 'warn' : 'brand';
  }
  if (ceiling != null) {
    if (ceiling <= 0) return 'brand';
    return pct >= ceiling ? 'down' : pct >= ceiling * 0.7 ? 'warn' : 'brand';
  }
  return pct >= 80 ? 'down' : pct >= 50 ? 'warn' : 'brand';
};

/**
 * 资金账户大屏 — 全部走真实后端,无 mock。
 *
 * 实时性(§9.4):
 *  - 价格 / 持仓现价 / 浮动 PnL / 净值:已接 WS `quote` 频道,前端逐 tick 本地计算
 *    (持仓行 + KPI 各自订阅 quote store;口径见 position-pnl.ts,XAUUSD 精确)。
 *  - K 线:GET .../main-account/trendbars,仍 REST 周期刷新(trendbar WS 尚未接入,留 backlog)。
 *  - 余额 / 当日已实现 / 风控水位:dashboard 5s 轮询(非逐 tick 量,够用)。
 *  - 两端点在主账户未配置/未连接时返 503 → 优雅降级显示"主账户未连接"。
 *
 * 待实现(留 backlog):
 *  - K 线逐 tick:接 WS `trendbar` 频道(snapshot + update),替换 15s 轮询。
 *  - 详细合约规格(点差/佣金/杠杆):GET /admin/api/v1/symbols/:id/details(ProtoOaSymbolByIdReq 2116)。
 */
export function LiveScreen() {
  const qc = useQueryClient();
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('m1');
  const dashQ = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: adminDashboard.get,
    refetchInterval: 5_000,
  });
  const data = dashQ.data;

  // §9.4 主账户 K 线(默认 XAUUSD/m1/200);503 不重试(主账户未配置/未连接)
  const trendbarsQ = useQuery({
    queryKey: ['admin', 'main-account', 'trendbars', chartPeriod, 200],
    queryFn: () => adminMainAccount.trendbars({ period: chartPeriod, count: 200 }),
    refetchInterval: 15_000,
    retry: (n, err) => shouldRetry(err) && !isMainAccountUnavailable(err) && n < 2,
  });
  // §9.4 主账户当前持仓
  const positionsQ = useQuery({
    queryKey: ['admin', 'main-account', 'positions'],
    queryFn: adminMainAccount.positions,
    refetchInterval: 10_000,
    retry: (n, err) => shouldRetry(err) && !isMainAccountUnavailable(err) && n < 2,
  });
  // 各用户虚拟持仓(v3 主账户净仓无单一 user_id,谁持什么仓走 virtual_legs,每行带用户ID)
  const userPositionsQ = useQuery({
    queryKey: ['admin', 'user-positions'],
    queryFn: adminMainAccount.userPositions,
    refetchInterval: 10_000,
  });
  // 真实账户容量与按品种方向性敞口:资金账户大屏承接原独立虚拟账户总览页里的风险明细。
  const capQ = useQuery({
    queryKey: ['admin', 'real-account', 'capacity'],
    queryFn: adminRealAccount.capacity,
    refetchInterval: 5_000,
  });

  const positionItems = positionsQ.data?.items ?? [];
  const userPositionItems = userPositionsQ.data?.items ?? [];

  // Trendbar(ts/open/high/low/close/volume)→ CandleChart 的 Candle(o/h/l/c/v)
  const candles: Candle[] = (trendbarsQ.data?.bars ?? []).map((b) => ({
    o: b.open,
    h: b.high,
    l: b.low,
    c: b.close,
    v: b.volume,
  }));

  // 持仓 → CandleChart 标记线(只标当前 K 线品种的持仓,按 entryPrice 画线)
  const chartSymbolId = trendbarsQ.data?.symbolId;
  const candleMarkers = positionItems
    .filter((p) => chartSymbolId == null || p.symbolId === chartSymbolId)
    .map((p) => ({
      price: p.entryPrice,
      label: `${p.side} ${(p.volume / 100).toFixed(2)}`,
      side: p.side,
    }));

  // MainAccountPosition → PositionsTable 的 MainPosition(现价/浮盈由各行订阅行情自算)
  const mainPositions: MainPosition[] = positionItems.map(toMainPosition);

  // 订阅需要的品种实时行情:持仓品种 + 当前 K 线品种 + 默认 XAUUSD(41)。
  // 仅订阅、不在本组件读 store —— 逐 tick 的 rerender 限制在 LiveKpis / 持仓行内部,
  // 不会重渲染整个大屏(K 线 / 风控水位等)。
  const subSymbols = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(positionsQ.data?.items ?? []).map((p) => p.symbolId),
            ...(userPositionsQ.data?.items ?? []).map((p) => p.symbolId),
            chartSymbolId,
            '41',
          ].filter((s): s is string => Boolean(s)),
        ),
      ),
    [positionsQ.data, userPositionsQ.data, chartSymbolId],
  );
  useQuoteStream(subSymbols);

  return (
    <div className="p-6 space-y-4 max-w-none">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Monitor className="h-6 w-6 text-brand" />
          <div>
            <h1 className="text-xl font-bold text-ink-0">资金账户大屏 · IC Markets</h1>
            <div className="text-xs text-ink-2 mt-0.5 flex items-center gap-2">
              <span className="num">账户 {data?.mainAccount.accountLogin ?? '——'}</span>
              <span className="text-ink-3">·</span>
              <Pill tone={dashQ.isError ? 'down' : 'up'}>
                {dashQ.isError ? '后端连接异常' : '价格·持仓·盈亏实时推送'}
              </Pill>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ForceLiquidateButton variant="compact" />
          <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ['admin'] })}>
            <RefreshCw className="h-4 w-4" />
            手动刷新
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Camera className="h-4 w-4" />
            导出截图
          </Button>
        </div>
      </header>

      {/* 主交易账户实时区 — 净值/浮动盈亏由实时行情逐 tick 重算 */}
      <LiveKpis main={data?.mainAccount} positions={positionItems} />
      <PerformanceOverview />

      {/* 今日对账 — 真实资金账户 vs App 用户虚拟账本结算口径 */}
      <TodayReconciliation
        main={data?.mainAccount}
        appUsersToday={data?.appUsersToday}
        detach={normalizeDetachState(data?.riskLevels?.detached)}
      />

      {/* 资金账户列表 — 保留多账户监控,但不再用总额 KPI 干扰主账户视图 */}
      <LiveAccountsOverview accounts={data?.liveAccounts ?? []} />

      {/* 保证金仪表盘 + 风控水位(并排) */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MarginGaugeCard main={data?.mainAccount} />
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>风控水位</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ProgressBar label="保证金占用" value={data?.riskLevels.marginUsagePct ?? 0} tone={data ? riskTone(data.riskLevels.marginUsagePct) : 'brand'} />
            <ProgressBar
              label="总持仓占用"
              value={data?.riskLevels.totalPositionPct ?? 0}
              tone={data ? riskTone(data.riskLevels.totalPositionPct) : 'brand'}
              hint={data?.riskLevels.detached?.active ? '(真实口径,不含已脱钩持仓)' : undefined}
            />
            {/* 脱钩期真实口径归零而虚拟持仓一手没少,虚拟口径是唯一还能反映"平台实际背着多少"的量 */}
            {data?.riskLevels.virtualTotalPositionPct != null && (
              <ProgressBar
                label="总持仓占用 · 虚拟口径"
                value={data.riskLevels.virtualTotalPositionPct}
                tone={riskTone(data.riskLevels.virtualTotalPositionPct)}
                hint="(全体用户虚拟持仓,含已脱钩;与上一条同分母)"
              />
            )}
            <ProgressBar
              label="当日亏损"
              value={data?.riskLevels.dailyLossPct ?? 0}
              tone={data ? riskTone(data.riskLevels.dailyLossPct, false, 70, data.riskLevels.maxDailyLossPercent) : 'brand'}
              hint={
                data && data.riskLevels.maxDailyLossPercent <= 0
                  ? '(未设置上限)'
                  : `(>${data?.riskLevels.maxDailyLossPercent ?? 5}% 急停)`
              }
            />
            <ProgressBar
              label="净值水位"
              value={data?.riskLevels.equityPct ?? 100}
              tone={data ? riskTone(data.riskLevels.equityPct, true, data.riskLevels.equityFloorPercent) : 'up'}
              hint={
                data && data.riskLevels.equityFloorPercent <= 0
                  ? '(未设置下限)'
                  : `(>${data?.riskLevels.equityFloorPercent ?? 70}% 健康)`
              }
            />
            <ProgressBar label="API 错误率" value={data?.riskLevels.apiErrorRatePct ?? 0} tone={data ? riskTone(data.riskLevels.apiErrorRatePct) : 'brand'} />
          </CardContent>
        </Card>
      </section>

      <DirectionalExposureCard cap={capQ.data} isLoading={capQ.isLoading} />

      {/* K 线 + 合约规格 — K 线走真实 trendbars 端点;合约规格走真实 symbols 列表 */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-9">
          <MainAccountCandle
            candles={candles}
            markers={candleMarkers}
            period={chartPeriod}
            onPeriodChange={setChartPeriod}
            isLoading={trendbarsQ.isLoading}
            unavailable={isMainAccountUnavailable(trendbarsQ.error)}
            error={trendbarsQ.isError && !isMainAccountUnavailable(trendbarsQ.error)}
          />
        </div>
        <div className="lg:col-span-3">
          <SymbolSpec />
        </div>
      </section>

      {/* 主账户当前持仓 — 走真实 main-account/positions 端点 */}
      <MainAccountPositions
        positions={mainPositions}
        openCountHint={data?.mainAccount.openPositionsCount}
        isLoading={positionsQ.isLoading}
        unavailable={isMainAccountUnavailable(positionsQ.error)}
        error={positionsQ.isError && !isMainAccountUnavailable(positionsQ.error)}
      />

      {/* 各用户虚拟持仓 — v3 主账户净仓无单一 user_id,谁持什么仓走 virtual_legs(每行带用户ID) */}
      <UserPositionsTable positions={userPositionItems} />

      {/* 当日跟单流水 — 从 signals API 拉真实数据 */}
      <RecentSignalsStream />
    </div>
  );
}

// ─── 4 KPI(实时净值 / 浮动盈亏逐 tick)─────────────────────────
//
// 余额走 dashboard(5s 轮询,够用);净值 / 浮动盈亏改为前端用实时行情
// (quote WS)对所有 open 持仓本地汇总,逐 tick 跳动 —— 与 cTrader 官方 App 口径一致。
// cTrader Open API 不推送 equity/浮盈(ProtoOATrader 只有 balance、netUnrealizedPnL 仅在
// GetPositionUnrealizedPnL 请求应答里给),官方 App 亦是客户端用实时报价逐 tick 重算,故此处同法。
// ★ 净口径:每仓 = 毛价差(computePositionLive)+ swap + commission(cTrader netUnrealizedPnL 定义,
//   不含平仓佣金),与券商 App「净盈亏/净值」一致;仅算毛价差会系统性差 Σ(swap+commission)。
// 无持仓时浮盈恒为 0;有持仓但行情未到时回退 dashboard 权威值。
// ⚠️ 实时汇总用 XAUUSD 口径(见 position-pnl.ts);系统当前仅 XAUUSD,精确。
function LiveKpis({
  main,
  positions,
}: {
  main: DashboardMainAccount | undefined;
  positions: MainAccountPosition[];
}) {
  const quotes = useQuoteStore((s) => s.quotes);

  // 实时总浮盈。无持仓→0;有持仓但全无行情→null(回退 dashboard 权威值)。
  let liveFloating: number | null = positions.length === 0 ? 0 : null;
  if (positions.length > 0) {
    let sum = 0;
    let any = false;
    for (const p of positions) {
      const { unrealizedPnl } = computePositionLive(
        { side: p.side, volume: p.volume, entryPrice: p.entryPrice },
        quotes[p.symbolId],
      );
      // 与 cTrader 官方 App 同口径:净浮盈 = 毛价差 + 库存费 + 已记佣金(不含平仓佣金)。
      const net = computeNetPnl(unrealizedPnl, p.swap, p.commission);
      if (net != null) {
        sum += net;
        any = true;
      }
    }
    if (any) liveFloating = sum;
  }

  const balanceNum = main ? moneyToNumber(main.balance) : 0;
  const floatingNum = liveFloating ?? (main ? moneyToNumber(main.floatingPnl) : 0);
  const equityNum =
    liveFloating != null ? balanceNum + liveFloating : main ? moneyToNumber(main.equity) : 0;
  const openPositionsCount = positions.length > 0 ? positions.length : main?.openPositionsCount ?? 0;

  const flash = usePriceFlash(floatingNum);
  const flashStyle: React.CSSProperties = {
    backgroundColor:
      flash === 'up' ? 'rgba(16,185,129,0.12)' : flash === 'down' ? 'rgba(239,68,68,0.12)' : 'transparent',
    transition: 'background-color 300ms ease-out',
    borderRadius: 12,
  };

  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KpiCard label="账户余额" tone="neutral" value={fmtMoney(main?.balance)} />
      <div style={flashStyle}>
        <KpiCard label="实时净值" tone={floatingNum >= 0 ? 'up' : 'down'} value={`$${fmtNum(equityNum)}`} />
      </div>
      <div style={flashStyle}>
        <KpiCard
          label="浮动盈亏"
          tone={floatingNum >= 0 ? 'up' : 'down'}
          value={fmtPnLNumber(floatingNum)}
          sub={`保证金 ${fmtMoney(main?.marginUsed)} · ${fmtPct(main?.marginUsagePct)}`}
        />
      </div>
      <KpiCard
        label="当前持仓"
        tone="neutral"
        value={`${openPositionsCount} 笔`}
        sub={`已用保证金 ${fmtMoney(main?.marginUsed)} · ${fmtPct(main?.marginUsagePct)}`}
      />
    </section>
  );
}

// ─── 今日对账区 ────────────────────────────────────────────────
//
// 真实资金账户是真金白银现金流;App 用户是虚拟账本结算。两边用于核对交易链路,
// 不能简单相加。服务费是用户侧交易服务费,不是 broker 真实佣金。
function fmtFormulaTerm(m: Money | null | undefined, mode: 'signed' | 'add' | 'subtract' = 'add'): string {
  if (!m || m.value == null) return '—';
  const value = moneyToNumber(m) * (mode === 'subtract' ? -1 : 1);
  if (mode === 'signed') return fmtMoney({ ...m, value: Number(m.value) }, { sign: true });
  const op = value < 0 ? '-' : '+';
  const absMoney: Money = { ...m, value: Math.abs(Number(m.value)) };
  return `${op} ${fmtMoney(absMoney)}`;
}

function hasMoney(m: Money | null | undefined): m is Money {
  return m?.value != null;
}

function TodayReconciliation({
  main,
  appUsersToday,
  detach,
}: {
  main: DashboardMainAccount | undefined;
  appUsersToday: DashboardAppUsersToday | undefined;
  detach: DashboardDetachedState;
}) {
  const realNet = moneyToNumber(main?.todayRealized);
  const realGross = moneyToNumber(main?.todayGrossPnl);
  const realCommission = moneyToNumber(main?.todayCommission);
  const realSettledCommission = moneyToNumber(main?.todaySettledCommission);
  const realSwap = moneyToNumber(main?.todaySwap);
  const userGross = moneyToNumber(appUsersToday?.grossPnl);
  const userFeeAllocation = moneyToNumber(appUsersToday?.feeAllocation);
  const userNet = moneyToNumber(appUsersToday?.netPnl);
  const tradeDayStart = fmtDateTime(appUsersToday?.tradeDayStart);
  const realFormula =
    hasMoney(main?.todayGrossPnl) && hasMoney(main?.todaySettledCommission) && hasMoney(main?.todaySwap) && hasMoney(main?.todayRealized)
      ? `${fmtFormulaTerm(main.todayGrossPnl, 'signed')} ${fmtFormulaTerm(main.todaySettledCommission)} ${fmtFormulaTerm(
          main.todaySwap,
        )} = ${fmtMoney(main.todayRealized, { sign: true })}`
      : '—';
  const userFormula =
    hasMoney(appUsersToday?.grossPnl) && hasMoney(appUsersToday?.feeAllocation) && hasMoney(appUsersToday?.serviceFee) && hasMoney(appUsersToday?.netPnl)
      ? `${fmtFormulaTerm(appUsersToday.grossPnl, 'signed')} ${fmtFormulaTerm(appUsersToday.feeAllocation)} ${fmtFormulaTerm(
          appUsersToday.serviceFee,
          'subtract',
        )} = ${fmtMoney(appUsersToday.netPnl, { sign: true })}`
      : '—';

  return (
    <section className="space-y-2">
      <div className="rounded-md border border-line bg-bg-2 px-3 py-2 text-xs text-ink-2">
        今日口径:按交易时段配置的兜底时间翻篇,不是自然日 00:00。当前交易日起点:
        <span className="num ml-1 text-ink-1">{tradeDayStart}</span>
      </div>
      {/* 脱钩期两张卡本就对不上:脱钩腿按中价结算、不向真实账户发单,右侧有而左侧无。
          不写这句,管理员每天都会看到一张"数据异常"的卡并反复排查。 */}
      {detach.active && (
        <div className="rounded-md border border-down/40 bg-down/5 px-3 py-2 text-xs leading-relaxed text-down-dim">
          <span className="font-semibold">
            虚实已脱钩(自 {fmtDateTime(detach.since)},仍在持 {detach.legs} 条)
          </span>
          :脱钩持仓平仓时按中价结算、不向真实账户发单,
          故右侧「App 用户结算」包含左侧「真实资金账户」没有对应真实成交的部分。
          <span className="font-semibold">两边对不上是预期内的,不必排查</span>,
          差额约等于平台对脱钩持仓承担的盈亏。脱钩前已结算的部分仍两边对应。
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-brand" />
              今日对账 · 真实资金账户
              {detach.active && (
                <span className="text-xs font-normal text-ink-3">仅覆盖仍在对冲的部分</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <MetricBox label="真实毛盈亏" value={fmtMoney(main?.todayGrossPnl, { sign: true })} tone={realGross} />
              <MetricBox label="净额计入手续费" value={fmtMoney(main?.todaySettledCommission, { sign: true })} tone={realSettledCommission} />
              <MetricBox label="库存费" value={fmtMoney(main?.todaySwap, { sign: true })} tone={realSwap} />
              <MetricBox label="真实净已实现" value={fmtMoney(main?.todayRealized, { sign: true })} tone={realNet} />
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <InfoLine label="真实总手续费" value={fmtMoney(main?.todayCommission, { sign: true })} tone={realCommission} />
              <InfoLine label="计算式" value={realFormula} tone={realNet} />
            </div>
            <p className="mt-3 text-xs leading-relaxed text-ink-3">
              真实总手续费用于和券商费用核对;真实净已实现只用已结算手续费计算,不含当前未平仓订单的开仓手续费。
              {detach.active && ' 脱钩期内本卡不含脱钩持仓的结算 —— 它们不在真实账户产生任何成交。'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-brand" />
              今日对账 · App 用户结算
              <DetachedBadge state={detach} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <MetricBox label="用户毛盈亏" value={fmtMoney(appUsersToday?.grossPnl, { sign: true })} tone={userGross} />
              <MetricBox label="用户交易服务费" value={fmtMoney(appUsersToday?.serviceFee)} />
              <MetricBox label="用户净盈亏" value={fmtMoney(appUsersToday?.netPnl, { sign: true })} tone={userNet} />
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <InfoLine label="费用分摊" value={fmtMoney(appUsersToday?.feeAllocation, { sign: true })} tone={userFeeAllocation} />
              <InfoLine label="计算式" value={userFormula} tone={userNet} />
              <InfoLine label="结算笔数" value={appUsersToday?.settlements ?? 0} />
              <InfoLine label="涉及用户" value={appUsersToday?.users ?? 0} />
            </div>
            <p className="mt-3 text-xs leading-relaxed text-ink-3">
              用户交易服务费是 App 虚拟账本服务费,不是券商真实手续费;用户净盈亏与 App 用户余额入账同口径。
              {detach.active && ' 脱钩期内本卡含脱钩持仓的结算,该部分盈亏由平台全额承担,左侧真实账户没有对应成交。'}
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function MetricBox({ label, value, tone }: { label: string; value: React.ReactNode; tone?: number }) {
  return (
    <div className="rounded-md border border-line bg-bg-2 px-3 py-3">
      <div className="text-xs text-ink-2">{label}</div>
      <div className={cn('mt-1 text-xl font-semibold num text-ink-0', toneClass(tone))}>{value}</div>
    </div>
  );
}

function InfoLine({ label, value, tone }: { label: string; value: React.ReactNode; tone?: number }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-bg-3 px-3 py-2">
      <span className="text-ink-2">{label}</span>
      <span className={cn('num font-medium text-ink-1', toneClass(tone))}>{value}</span>
    </div>
  );
}

function toneClass(value: number | undefined): string {
  if (value == null || value === 0) return 'text-ink-1';
  return value > 0 ? 'text-up' : 'text-down';
}

// ─── 保证金仪表盘卡 ────────────────────────────────────────────
//
// 保证金水平 = equity / 已用保证金 × 100(cTrader 口径);已用=0(无持仓)显示 ∞。
// 数据走 dashboard.mainAccount(5s 轮询的真实账户净值/保证金);可用保证金 = 净值 − 已用。
function MarginGaugeCard({ main }: { main: DashboardMainAccount | undefined }) {
  const equityN = main ? moneyToNumber(main.equity) : 0;
  const usedN = main ? moneyToNumber(main.marginUsed) : 0;
  const level = main && usedN > 0 ? (equityN / usedN) * 100 : main && usedN === 0 ? null : 0;
  const freeMargin: Money = { value: Math.round((equityN - usedN) * 100), digits: 2 };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-brand" />
          保证金
        </CardTitle>
      </CardHeader>
      <CardContent>
        <MarginGauge
          level={main ? level : 0}
          freeMargin={main ? freeMargin : undefined}
          usedMargin={main?.marginUsed}
        />
      </CardContent>
    </Card>
  );
}

// ─── 方向性风险(按品种)───────────────────────────────────────────
//
// 原独立虚拟账户总览页里仍有价值的风险明细迁移到资金账户大屏:
// 单一真实账户的持仓与容量风险应在同一监控屏里看。
function DirectionalExposureCard({
  cap,
  isLoading,
}: {
  cap: RealAccountCapacity | undefined;
  isLoading: boolean;
}) {
  const exposures = cap?.exposureBySymbol ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-brand" />
          方向性风险(按品种) · 同向集中度
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="px-4 py-6 text-center text-sm text-ink-3">方向性风险加载中…</div>
        ) : exposures.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-ink-3">当前无真实持仓</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">品种</th>
                  <th className="px-3 py-2 text-right font-normal">多(手)</th>
                  <th className="px-3 py-2 text-right font-normal">空(手)</th>
                  <th className="px-3 py-2 text-right font-normal">毛敞口(手)</th>
                  <th className="px-3 py-2 text-right font-normal">净敞口(手)</th>
                  <th
                    className="px-3 py-2 text-right font-normal"
                    title="某方向同向毛量占该品种毛敞口比例;越高=越一边倒"
                  >
                    同向集中度
                  </th>
                  <th className="px-3 py-2 text-right font-normal">笔数</th>
                  <th
                    className="px-3 py-2 text-right font-normal"
                    title={
                      '毛敞口里已脱钩(没有真实对冲支撑)的部分。容量卡的分级只按已对冲部分算,' +
                      '这一列告诉你毛敞口里有多少是平台在裸背'
                    }
                  >
                    其中已脱钩
                  </th>
                  <th
                    className="px-3 py-2 text-right font-normal"
                    title="跨品种可比:lots×合约规模×入场价。比不同品种的风险看这列,不要比手数"
                  >
                    名义价值($)
                  </th>
                </tr>
              </thead>
              <tbody>
                {exposures.map((e) => {
                  const conc = e.concentrationRatio;
                  const concTone =
                    conc >= 85 ? 'text-down' : conc >= 70 ? 'text-warn' : 'text-ink-1';
                  return (
                    <tr key={e.symbolId} className="border-t border-line row-hover">
                      <td className="px-3 py-2 text-ink-1">{e.symbolName ?? e.symbolId}</td>
                      <td className="px-3 py-2 text-right num text-up">{(e.longTotal / 100).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right num text-down">{(e.shortTotal / 100).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right num text-ink-1">{(e.grossExposure / 100).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right num text-ink-1">{(e.netExposure / 100).toFixed(2)}</td>
                      <td className={cn('px-3 py-2 text-right num font-medium', concTone)}>
                        {conc.toFixed(0)}%
                      </td>
                      <td className="px-3 py-2 text-right num text-ink-3">{e.positionsCount}</td>
                      <td className="px-3 py-2 text-right num">
                        {e.detachedGrossExposure == null ? (
                          <span className="text-ink-3" title="后端未返回该字段">
                            —
                          </span>
                        ) : e.detachedGrossExposure > 0 ? (
                          <span
                            className="text-down font-medium"
                            title="该品种这部分敞口已无真实仓支撑,平台自担盈亏"
                          >
                            {(e.detachedGrossExposure / 100).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-ink-3">0.00</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right num text-ink-1">
                        {e.notionalUnavailable || e.grossNotionalUsdCents == null ? (
                          <span className="text-ink-3" title="缺合约规模,名义价值不可算">
                            —
                          </span>
                        ) : (
                          fmtMoney({ value: e.grossNotionalUsdCents, digits: 2 })
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── §9.4 主账户 K 线区 ────────────────────────────────────────
//
// CandleChart 自实现 SVG 蜡烛图,props 取 Candle{o,h,l,c,v} + markers{price,label,side}。
// 后端 Trendbar 用 open/high/low/close/volume,故在 LiveScreen 内做字段名映射。
// 503(主账户未配置/未连接)/ 其他错误 / 加载中分别降级,避免崩溃。
interface MainAccountCandleProps {
  candles: Candle[];
  markers: Array<{ price: number; label: string; side: 'BUY' | 'SELL' }>;
  period: ChartPeriod;
  onPeriodChange: (period: ChartPeriod) => void;
  isLoading: boolean;
  unavailable: boolean;
  error: boolean;
}

function MainAccountCandle({
  candles,
  markers,
  period,
  onPeriodChange,
  isLoading,
  unavailable,
  error,
}: MainAccountCandleProps) {
  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>XAUUSD K 线</span>
          <span className="flex items-center gap-2">
            <span className="inline-flex rounded-md border border-line bg-bg-2 p-0.5">
              {CHART_PERIODS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => onPeriodChange(p.value)}
                  className={cn(
                    'h-7 px-2.5 text-xs font-medium transition-colors rounded',
                    period === p.value ? 'bg-brand text-white' : 'text-ink-2 hover:bg-bg-3 hover:text-ink-1',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </span>
            <span className="text-xs font-normal text-ink-3">刷新 15s/次</span>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-center min-h-[340px]">
        {unavailable ? (
          <CenterHint title="主账户未连接" desc="主账户未配置或 cTrader live 连接未建立,无法获取 K 线。请在 Settings 配置主账户并确认连接后重试。" />
        ) : error ? (
          <CenterHint title="K 线加载失败" desc="拉取主账户 K 线出错,请稍后手动刷新。" tone="down" />
        ) : isLoading ? (
          <CenterHint title="K 线加载中…" />
        ) : candles.length === 0 ? (
          <CenterHint title="暂无 K 线数据" desc="工作日开盘后(周一 19:00 北京时间)cTrader 才会推真实蜡烛数据。" />
        ) : (
          <CandleChart candles={candles} markers={markers} height={320} className="w-full" />
        )}
      </CardContent>
    </Card>
  );
}

// ─── §9.4 主账户当前持仓区 ─────────────────────────────────────
//
// PositionsTable props 取 MainPosition{positionId,side,volume,openPrice,...};
// 后端 MainAccountPosition 用 entryPrice/slPrice/tpPrice,且不返回 currentPrice/unrealizedPnl
// (需行情 WS 叠加),故映射后这两字段留空(组件渲染「—」/「实时」)。
interface MainAccountPositionsProps {
  positions: MainPosition[];
  openCountHint?: number;
  isLoading: boolean;
  unavailable: boolean;
  error: boolean;
}

function MainAccountPositions({ positions, openCountHint, isLoading, unavailable, error }: MainAccountPositionsProps) {
  if (unavailable || error || isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>主账户当前持仓</span>
            <span className="text-xs text-ink-3">
              开仓笔数:<span className="num text-ink-1">{openCountHint ?? 0}</span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {unavailable ? (
            <CenterHint title="主账户未连接" desc="主账户未配置或 cTrader live 连接未建立,无法获取持仓明细。" minH="100px" />
          ) : error ? (
            <CenterHint title="持仓加载失败" desc="拉取主账户持仓出错,请稍后手动刷新。" tone="down" minH="100px" />
          ) : (
            <CenterHint title="持仓加载中…" minH="100px" />
          )}
        </CardContent>
      </Card>
    );
  }
  return <PositionsTable positions={positions} />;
}

/** Trendbar/持仓区共用的居中提示 */
function CenterHint({
  title,
  desc,
  tone = 'neutral',
  minH = '300px',
}: {
  title: string;
  desc?: string;
  tone?: 'neutral' | 'down';
  minH?: string;
}) {
  return (
    <div className="flex w-full items-center justify-center text-center" style={{ minHeight: minH }}>
      <div className="space-y-2">
        <div className={tone === 'down' ? 'text-sm text-down' : 'text-sm text-ink-2'}>{title}</div>
        {desc && <div className="text-xs text-ink-3 max-w-md mx-auto">{desc}</div>}
      </div>
    </div>
  );
}

/** MainAccountPosition → PositionsTable 的 MainPosition;currentPrice/unrealizedPnl 后端不返回,留空 */
function toMainPosition(p: MainAccountPosition): MainPosition {
  return {
    positionId: p.positionId,
    symbolId: p.symbolId,
    side: p.side,
    volume: p.volume,
    openPrice: p.entryPrice,
    sl: p.slPrice,
    tp: p.tpPrice,
    openedAt: p.openedAt,
    swap: p.swap,
    commission: p.commission,
    // 现价 / 浮动 PnL 不在此映射:由 PositionsTable 每行订阅 symbolId 的实时行情本地计算;
    // 净盈亏 = 浮动 PnL + swap + commission(后端已在 positions 端点给出 swap/commission)
  };
}

// ─── 全部资金账户汇总区 ────────────────────────────────────────
//
// 大屏顶部新增模块,展示所有 `account_type='live'` 且 `status='in_use'` 的账户摘要,
// 包括主账户(isMain=true,参与跟单)和监控账户(防爆仓 / 防管理员充错账号)。
// 该区只展示,不提供切换按钮 — 主账户配置入口在 Settings 页。
interface LiveAccountsOverviewProps {
  accounts: LiveAccountSummary[];
}

function LiveAccountsOverview({ accounts }: LiveAccountsOverviewProps) {
  const count = accounts.length;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-brand" />
          <span>资金账户列表</span>
          <Pill tone="neutral">{count} 个</Pill>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {accounts.length === 0 ? (
          <div className="rounded-md border border-dashed border-line bg-bg-3 px-3 py-6 text-center text-sm text-ink-3">
            暂无资金账户配置
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">账户号</th>
                  <th className="px-3 py-2 text-left">角色</th>
                  <th className="px-3 py-2 text-right">余额</th>
                  <th className="px-3 py-2 text-right">净值</th>
                  <th className="px-3 py-2 text-right">浮动盈亏</th>
                  <th className="px-3 py-2 text-left">连接</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <LiveAccountRow key={a.ctidTraderAccountId} a={a} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[11px] leading-relaxed text-ink-3">
          这里只做多资金账户监控。交易与对账口径以上方主交易账户和今日对账为准。
        </p>
      </CardContent>
    </Card>
  );
}

function LiveAccountRow({ a }: { a: LiveAccountSummary }) {
  const floatingNum = moneyToNumber(a.floatingPnl);
  const floatingTone: 'up' | 'down' | 'neutral' =
    floatingNum > 0 ? 'up' : floatingNum < 0 ? 'down' : 'neutral';
  const toneText = floatingTone === 'up' ? 'text-up' : floatingTone === 'down' ? 'text-down' : 'text-ink-1';
  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2 num text-ink-0">{a.accountLogin}</td>
      <td className="px-3 py-2">
        {a.isMain ? (
          <Pill tone="brand" className="gap-1">
            <Star className="h-3 w-3" />
            主账户
          </Pill>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </td>
      <td className="px-3 py-2 num text-right text-ink-1">{fmtMoney(a.balance)}</td>
      <td className="px-3 py-2 num text-right text-ink-1">{fmtMoney(a.equity)}</td>
      <td className={`px-3 py-2 num text-right ${toneText}`}>
        {fmtMoney(a.floatingPnl, { sign: true })}
      </td>
      <td className="px-3 py-2">
        {a.connectionStatus === 'connected' ? (
          <Pill tone="up">已连接</Pill>
        ) : (
          <Pill tone="down">未连接</Pill>
        )}
      </td>
    </tr>
  );
}
